import { Component } from '@theme/component';
import { isDesktopBreakpoint } from '@theme/utilities';

/**
 * Scales its image in place following the pointer on hover, instead of
 * opening the zoom dialog. Desktop only (`isDesktopBreakpoint`) — mobile
 * keeps the tap-to-open `<zoom-dialog>` behavior untouched.
 *
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The image element to zoom.
 *
 * @extends Component<Refs>
 */
export class HoverZoomImage extends Component {
  requiredRefs = ['image'];

  connectedCallback() {
    super.connectedCallback();
    if (!isDesktopBreakpoint()) return;

    this.addEventListener('pointerenter', this.#handlePointerEnter);
    this.addEventListener('pointermove', this.#handlePointerMove);
    this.addEventListener('pointerleave', this.#handlePointerLeave);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.removeEventListener('pointermove', this.#handlePointerMove);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
  }

  /** @param {PointerEvent} event */
  #handlePointerEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.classList.add('is-zoomed');
    this.#updateOrigin(event);
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    if (event.pointerType !== 'mouse') return;
    this.#updateOrigin(event);
  };

  #handlePointerLeave = () => {
    this.classList.remove('is-zoomed');
  };

  /** @param {PointerEvent} event */
  #updateOrigin(event) {
    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    this.style.setProperty('--hover-zoom-x', `${x}%`);
    this.style.setProperty('--hover-zoom-y', `${y}%`);
  }
}

if (!customElements.get('hover-zoom-image')) {
  customElements.define('hover-zoom-image', HoverZoomImage);
}
